import {
    formatPurchaseOrderDate,
    formatPurchaseOrderDateTime,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { FALLBACK_TEXT } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';

import { DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT } from './crossDockPurchaseOrderCreateValidation';

export const CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH = '/logistics/cross-dock-purchase-orders';

export function getCrossDockPurchaseOrderViewPath(id) {
    return `/logistics/view-cross-dock-purchase-order/${id}`;
}

export function parseCrossDockPurchaseOrderId(id) {
    if (id === null || id === undefined || id === '') {
        return null;
    }
    const normalized = String(id).trim();
    if (!/^\d+$/.test(normalized)) {
        return null;
    }
    const parsed = Number.parseInt(normalized, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function formatCrossDockPurchaseOrderScheduledDelivery(order) {
    const date = formatPurchaseOrderDate(order?.scheduledDeliveryDate, '');
    const time = order?.scheduledDeliveryTime;
    if (date && time) {
        return `${date} ${time}`;
    }
    return date || time || FALLBACK_TEXT;
}

export function getCrossDockPurchaseOrderPoMapIdLabel(poMap) {
    if (poMap?.purchaseOrderId != null) {
        return `PO ${poMap.purchaseOrderId}`;
    }
    if (poMap?.packagingPurchaseOrderId != null) {
        return `PPO-${poMap.packagingPurchaseOrderId}`;
    }
    return FALLBACK_TEXT;
}

export function formatCrossDockPurchaseOrderPoMapLabel(poMap) {
    const idLabel = getCrossDockPurchaseOrderPoMapIdLabel(poMap);
    if (idLabel === FALLBACK_TEXT) {
        return FALLBACK_TEXT;
    }
    const vendorName =
        poMap?.vendorName === null || poMap?.vendorName === undefined ? '' : String(poMap.vendorName).trim();
    if (!vendorName) {
        return idLabel;
    }
    return `${idLabel} - ${vendorName}`;
}

export function isPackagingCrossDockPurchaseOrderPoMap(poMap) {
    return poMap?.packagingPurchaseOrderId != null || poMap?.poType === 'PACKAGING';
}

export function getCrossDockPurchaseOrderPoMapSelectionKey(poMap) {
    if (poMap?.packagingPurchaseOrderId != null) {
        return `ppo:${poMap.packagingPurchaseOrderId}`;
    }
    if (poMap?.purchaseOrderId != null) {
        return `po:${poMap.purchaseOrderId}`;
    }
    return null;
}

export function getCrossDockPurchaseOrderPoMapHref(poMap) {
    if (poMap?.purchaseOrderId != null) {
        return `/procurement/viewPurchaseOrder-v2/${poMap.purchaseOrderId}`;
    }
    if (poMap?.packagingPurchaseOrderId != null) {
        return `/packaging/viewPurchaseOrder-v2/${poMap.packagingPurchaseOrderId}`;
    }
    return null;
}

export function formatCrossDockPurchaseOrderCreatedOn(createdAt) {
    return formatPurchaseOrderDateTime(createdAt, FALLBACK_TEXT);
}

const NON_EDITABLE_CROSS_DOCK_PURCHASE_ORDER_STATUSES = new Set(['RECEIVED', 'COMPLETE', 'CANCELLED']);

export function canEditCrossDockPurchaseOrder(order) {
    if (!order) {
        return false;
    }
    const status = String(order.status ?? '')
        .trim()
        .toUpperCase();
    return !NON_EDITABLE_CROSS_DOCK_PURCHASE_ORDER_STATUSES.has(status);
}

export function canCancelCrossDockPurchaseOrder(order) {
    if (!order) {
        return false;
    }
    const status = String(order.status ?? '')
        .trim()
        .toUpperCase();
    return status !== 'CANCELLED';
}

export function buildDraftFromCrossDockPurchaseOrder(order) {
    if (!order) {
        return null;
    }

    return {
        ...DEFAULT_CROSS_DOCK_PURCHASE_ORDER_DRAFT,
        crossDockId: order.crossDockId != null ? String(order.crossDockId) : '',
        facilityId: order.facilityId != null ? String(order.facilityId) : '',
        dockDoor: order.dockDoor ?? '',
        scheduledDeliveryDate: order.scheduledDeliveryDate ?? '',
        scheduledDeliveryTime: order.scheduledDeliveryTime ?? '',
        shipDate: order.shipDate ?? '',
        minTemp: order.minTemp != null ? String(order.minTemp) : '',
        maxTemp: order.maxTemp != null ? String(order.maxTemp) : '',
        specialInstructions: order.specialInstructions ?? '',
        additionalFees: (order.additionalFees ?? []).map((fee) => ({
            id: fee.id,
            chargeType: fee.chargeType,
            quantity: fee.quantity != null ? String(fee.quantity) : '',
            unitRate: fee.unitRate != null ? String(fee.unitRate) : '',
            totalRate: fee.totalRate ?? 0,
        })),
        poMaps: order.poMaps ?? [],
    };
}
